import { useQueries, useQuery } from "@tanstack/react-query";

import { _isSafePayloadPath, apiFetchPayloadText } from "@/api/client";

/**
 * Whether a server-supplied `download_url` is one this app will fetch WITH THE
 * USER'S CREDENTIALS.
 *
 * The check itself is `client.ts`'s `_isSafePayloadPath` — the same pin
 * `apiDownload` and `apiFetchPayloadText` already apply at the boundary
 * (flowstack#624), so this adds no second opinion about what is safe; there is
 * exactly one such rule and it lives there. What this name adds is the ability
 * to ask the question BEFORE fetching, which two callers need:
 *
 * - **A view can render a non-conforming address as its own state** instead of
 *   as a generic "could not load", so a poisoned or mis-generated URL is
 *   visible as what it is rather than looking like a network fault.
 * - **Speculative prefetching can skip it.** A prefetch swallows its outcome by
 *   design (it is work for a point the user may never reach), and a rejected
 *   address is the one outcome that must not be swallowed — so it is never
 *   started (Rupert PR-1193 r0).
 *
 * A conforming path is origin-relative by construction, so a credentialed fetch
 * can only target the configured API origin. `download_url` is server-response
 * data crossing a trust boundary: this PR declines to trust the server's
 * `geometry_source` vocabulary, and a URL gets the same standard.
 */
export function isViewablePayloadUrl(downloadUrl: string): boolean {
  return _isSafePayloadPath(downloadUrl);
}

/**
 * Largest structure the in-page viewer will fetch and parse, in bytes.
 *
 * Deliberately far below the server's download cap
 * (`FLOWSTACK_MAX_PAYLOAD_DOWNLOAD_BYTES`, 64 MiB by default): that number
 * bounds what the API process will *serve*, while this one bounds what a
 * browser tab will hold as text, hand to a parser, and turn into WebGL
 * geometry — three copies, none of which the download path pays for.
 *
 * 8 MiB clears the structures FlowStack actually catalogues by orders of
 * magnitude: the ligand-scale SDF/mol2/pdb files that `Molecule`,
 * `ConformerEnsemble` and `ProteinStructure` carry are kilobytes to low
 * megabytes. What it refuses is the trajectory-scale payload that would lock
 * the tab up rather than draw — and it refuses it with a message, which is the
 * point. Compiled in rather than configured because it is a property of the
 * client's memory and the renderer, not of a deployment; the server-side knob
 * stays the operational one.
 */
export const MAX_VIEWER_PAYLOAD_BYTES = 8 * 1024 * 1024;

export function payloadTextQueryKey(downloadUrl: string) {
  return ["artifacts", "payload-text", downloadUrl] as const;
}

/**
 * Read one artifact payload as text for in-page rendering.
 *
 * Disabled until `downloadUrl` is non-null, so a page can call it
 * unconditionally while the artifact detail is still loading or while the
 * artifact turns out to have no viewable payload at all.
 *
 * `staleTime: Infinity` because the payload is content-addressed: the URL
 * embeds the artifact's `content_hash`, so the bytes behind it cannot change.
 * Re-fetching one would be pure cost.
 */
export function useArtifactPayloadText(downloadUrl: string | null) {
  return useQuery({
    queryKey: payloadTextQueryKey(downloadUrl ?? ""),
    enabled: downloadUrl !== null && downloadUrl !== "",
    queryFn: () => apiFetchPayloadText(downloadUrl as string, MAX_VIEWER_PAYLOAD_BYTES),
    staleTime: Infinity,
    // A 413 or a 404 is a property of the artifact, not a transient fault;
    // retrying re-fetches bytes that will be refused identically.
    retry: false,
  });
}

/**
 * Read SEVERAL payloads as text — a whole trajectory at once (flowstack#1327).
 *
 * Same key, same fetcher and the same `staleTime: Infinity` as
 * {@link useArtifactPayloadText}, so the two share a cache entry: a payload one
 * of them has already read costs the other nothing, and a URL that appears in
 * both is one query.
 *
 * **The total bytes are what a user scrubbing a scan end-to-end already
 * fetches** — the change is that they are asked for up front and in parallel
 * rather than one at a time behind a settle delay, which is what lets a frame
 * step cost no request at all. Each response is still bounded by
 * {@link MAX_VIEWER_PAYLOAD_BYTES}, and the count is bounded by the scan grid
 * the panel already draws one scrub position per.
 *
 * Results are POSITIONAL and every entry keeps its own status, because the
 * caller must be able to say why one particular point has no frame while its
 * neighbours do. Nothing here aggregates a failure into "the trajectory
 * failed".
 */
export function useArtifactPayloadTexts(downloadUrls: readonly string[]) {
  return useQueries({
    queries: downloadUrls.map((url) => ({
      queryKey: payloadTextQueryKey(url),
      queryFn: () => apiFetchPayloadText(url, MAX_VIEWER_PAYLOAD_BYTES),
      staleTime: Infinity,
      retry: false,
    })),
  });
}

// NOTE (flowstack#1327): `prefetchArtifactPayloadText` — the one-deep neighbour
// ring VIZ U5.2b warmed the scrub with — is DELETED, not left unused. Its whole
// purpose was to make the next step's fetch already-done, and the scan panel now
// resolves every point's geometry up front, so there is no next step to warm and
// no caller left. Keeping a speculative-fetch helper nobody calls would invite
// the next surface to reach for progressive fetching without re-deciding whether
// it wants the blank frame that comes with it.
